import React from "react";
import { createPortal } from "react-dom";
import { Box, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { CheckCircleOutline } from "@mui/icons-material";
import SuccessFeedbackDialog from "../../Shared/successFeedbackDialog";
import ErrorDialog from "../../Shared/ErrorDialog";
import { featureFlags, locationiqApiKey } from "../../../config/featureConfig";
import { vendorMainColumnMaxWidthSx } from "../../../config/vendorContentMaxWidth";
import { PRIMARY } from "./constants";
import { profileCardSx, profilePageSectionSpacing } from "./profileFieldStyles";
import { ProfileHeader } from "./components/ProfileHeader";
import { ProfileFormCard } from "./components/ProfileFormCard";
import EliminaProfilo from "./components/EliminaProfilo";
import { useVendorProfile } from "./hooks/useVendorProfile";
import { LOCATION_IQ_USER_MESSAGE_IT } from "./components/sede/SedeAziendale";

const ProfiloVendor = () => {
  const profile = useVendorProfile();

  return (
    <>
      <Box sx={{ width: "100%" }}>
          <Stack spacing={0.5} sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontFamily: "Rubik, sans-serif",
                fontWeight: 800,
                fontSize: { xs: "1.65rem", sm: "2rem" },
                color: PRIMARY,
                letterSpacing: "-0.02em",
              }}
            >
              Profilo
            </Typography>
            <Typography
              sx={{
                fontFamily: "Rubik, sans-serif",
                fontSize: "0.9375rem",
                color: "text.secondary",
                maxWidth: 560,
                lineHeight: 1.5,
              }}
            >
              Gestisci l&apos;identità aziendale del tuo E-Commerce su Wow Che Prezzi.
            </Typography>
          </Stack>

          {profile.showInitialProfileSpinner ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 200,
                py: 6,
              }}
            >
              <CircularProgress sx={{ color: "#0b3d87" }} />
            </Box>
          ) : (
            <>
              <Stack spacing={profilePageSectionSpacing} sx={{ width: "100%" }}>
                <ProfileHeader
                  companyName={profile.companyName}
                  email={profile.vendorForm.indirizzoEmail}
                  base64={profile.base64}
                  hasLogo={profile.showLogoPreview}
                />

                <Paper
                  component="form"
                  onSubmit={profile.handleSubmit}
                  elevation={0}
                  sx={{
                    ...profileCardSx,
                    ...vendorMainColumnMaxWidthSx,
                  }}
                >
                  <ProfileFormCard
                    isEditing={profile.isEditing}
                    isImpersonated={profile.isImpersonated}
                    onStartEdit={profile.handleStartEdit}
                    onCancelEdit={profile.handleCancelEdit}
                    vendorForm={profile.vendorForm}
                    fieldErrors={profile.fieldErrors}
                    onChange={profile.handleChange}
                    showLogoPreview={profile.showLogoPreview}
                    fileInputRef={profile.fileInputRef}
                    onFileChange={profile.handleFileChange}
                    onRemoveLogo={profile.handleRemoveLogo}
                    enableMetodiPagamento={featureFlags.enableMetodiPagamento}
                    metodiPagamenti={profile.metodiPagamenti}
                    paymentSelectedIds={profile.paymentSelectedIds}
                    togglePaymentMethodId={profile.togglePaymentMethodId}
                    enableLegalAddressValidation={featureFlags.enableSedeAziendaleValidationService}
                    sedeAddressValues={profile.sedeAddressValues}
                    onSedeFieldChange={profile.handleSedeFieldChange}
                    hasStoredAddress={profile.hasStoredAddress}
                    addressNeedsReverify={profile.addressNeedsReverify}
                    onSedeVerifiedMerge={profile.handleSedeVerifiedMerge}
                    onSedeResetToVerified={profile.handleSedeResetToVerified}
                    onGeocodeServiceUnavailable={() => profile.setGeocodeErrorOpen(true)}
                    locationiqApiKey={locationiqApiKey}
                    submitDisabled={profile.submitDisabled}
                  />
                </Paper>

                <EliminaProfilo />
              </Stack>
            </>
          )}
        </Box>

      {typeof document !== "undefined" && profile.saveLoading
        ? createPortal(
            <Box
              aria-busy="true"
              aria-live="polite"
              sx={{
                position: "fixed",
                inset: 0,
                width: "100vw",
                minHeight: "100vh",
                bgcolor: "rgba(0, 0, 0, 0.45)",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: (theme) => theme.zIndex.modal,
              }}
            >
              <CircularProgress color="primary" size={60} />
            </Box>,
            document.body
          )
        : null}

      <SuccessFeedbackDialog
        open={profile.successFeedback.open}
        onOk={profile.handleSuccessDialogOk}
        title={profile.successFeedback.title}
        message={profile.successFeedback.message}
        confirmText="Ok"
        icon={<CheckCircleOutline sx={{ color: "white", fontSize: 50 }} />}
      />
      <ErrorDialog
        open={profile.geocodeErrorOpen}
        onClose={() => profile.setGeocodeErrorOpen(false)}
        error={{ message: LOCATION_IQ_USER_MESSAGE_IT }}
      />
    </>
  );
};

export default ProfiloVendor;
